import React from 'react';
import { Stack } from '@fluentui/react';
import { PeopleTeam20Regular } from '@fluentui/react-icons';
import { usePanelMobileView } from 'common/components/Panel/usePanelMobileView';
import { Text } from 'common/components/Text';
import { useLocalizedStrings } from 'i18n';
import { FamilyMember } from 'mcsclient/models';
import { useFamily } from 'store/family/hooks/useFamily';
import { useNotificationsDispatch, useNotificationSettingsSummary } from 'store/notifications/useNotificationSettings';

import { stackTokens, useNotificationSettingsStyles } from '../NotificationSettings.styles';
import { NotificationSettingsSummary } from '../NotificationSettingsSummary';

import { FamilyMemberView } from './FamilyMemberView';

export const FamilyNotificationSettingsSummary = ({ onDrillDown }: { onDrillDown: (familyMember: FamilyMember) => void }) => {
  const { notificationSettingsSummary } = useNotificationSettingsSummary();
  const { updateFamilyNotificationSettings } = useNotificationsDispatch();
  const { t } = useLocalizedStrings();
  const { otherFamilyMembers } = useFamily();
  const { isMobileView } = usePanelMobileView();
  const classNames = useNotificationSettingsStyles();

  const onToggle = React.useCallback(
    (isEnabled: boolean) => {
      updateFamilyNotificationSettings({ isEnabled });
    },
    [updateFamilyNotificationSettings]
  );

  const text = t('OfficeShellPanel.NotificationSettings.Sections.Family.Text');
  const confirmSubtext = t('OfficeShellPanel.NotificationSettings.Sections.Family.TurnOffConfirm.Subtext');
  const confirmTitle = t('OfficeShellPanel.NotificationSettings.Sections.Family.TurnOffConfirm.Title');

  const familyNotificationsEnabled = notificationSettingsSummary?.familyNotificationSetting?.isEnabled ?? false;
  const familyNotificationsToggleable = notificationSettingsSummary?.familyNotificationSetting?.isToggleable ?? false;
  const toggleNotificationLabel = familyNotificationsEnabled
    ? t('OfficeShellPanel.NotificationSettings.Sections.On')
    : t('OfficeShellPanel.NotificationSettings.Sections.Off');

  return (
    <Stack tokens={stackTokens.gapMD}>
      <Stack tokens={isMobileView ? undefined : stackTokens.gapLG}>
        <NotificationSettingsSummary
          className={classNames.panelSettingSection}
          notificationType="Family"
          title={t('OfficeShellPanel.NotificationSettings.Sections.Family.Title')}
          iconComponent={PeopleTeam20Regular}
          text={isMobileView ? undefined : text}
          toggleNotificationLabel={toggleNotificationLabel}
          toggleAriaLabel={t('OfficeShellPanel.NotificationSettings.Sections.Family.Title')}
          notificationsEnabled={familyNotificationsEnabled}
          isToggleable={familyNotificationsToggleable}
          onToggleNotification={onToggle}
          turnOffConfirmStrings={{
            title: confirmTitle,
            subText: confirmSubtext,
          }}
        />
        {otherFamilyMembers?.map((member) => (
          <Stack.Item key={member.puid}>
            <FamilyMemberView
              member={member}
              onDrillDown={() => {
                onDrillDown(member);
              }}
            />
          </Stack.Item>
        ))}
      </Stack>
      {isMobileView && (
        <Stack.Item className={classNames.summary}>
          <Text variant="Caption2" block>
            {text}
          </Text>
        </Stack.Item>
      )}
    </Stack>
  );
};
